// Helpers
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";

// Types
import { Nullable } from "@Common/interfaces/types";
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { ISalesLog, SalesLogAnalytic } from "@DataPages/types/salesOperations/salesLog/model";
import { SalesMetricsFragment } from "@DataPages/api/fragments/salesLog/salesLog";

export async function getSalesLog(locationId: string, commissionCycleId: string): Promise<Nullable<ISalesLog[]>> {
	const query = gql`
		query getSalesLog($locationId: String!, $commissionCycleId: String!) {
			getSalesLog(locationId: $locationId, commissionCycleId: $commissionCycleId) {
				_id
				locationId
				commissionCycleId
				period
				createdAt
				createdBy
				updatedAt
				updatedBy
				saleDate
				dealNumber
				dealStatus
				dealType
				salesEmployeeId
				salesEmployeeName
				financeEmployeeId
				financeManagerName
				salesManagerId
				dealManagerName
				secondManagerName
				customerName
				customerNumber
				stockNumber
				age
				description
				saleType
				dealerFee
				frontGrossProfit
				backGrossProfit
				units
				frontProducts {
					product
					value
				}
				backProducts {
					product
					value
				}
				tradeACV
				tradeMiles
				franchiseName
			}
		}
	`;

	const variables = {
		commissionCycleId,
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesLog", ISalesLog[]> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSalesLog;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getSalesLogAnalytics(locationId: string, commissionCycleId: string): Promise<Nullable<SalesLogAnalytic>> {
	const query = gql`
		query getSalesLogAnalytics($locationId: String!, $commissionCycleId: String!) {
			getSalesLogAnalytics(locationId: $locationId, commissionCycleId: $commissionCycleId) {
				_id
				commissionCycleId
				locationId
				analytics {
					totalMonthDays
					new {
						franchiseName
						salesMetrics {
							...SalesMetrics
						}
					}
					preOwned {
						franchiseName
						salesMetrics {
							...SalesMetrics
						}
					}
					totals {
						salesMetrics {
							...SalesMetrics
						}
					}
					wholesale {
						salesMetrics {
							...SalesMetrics
						}
					}
				}
				dynamicPVRInfo {
					analyticReportName
					minValue
					maxValue
					average
				}
				pvrMaxGraphValues {
					maxNewVehiclesPVR
					maxPreOwnedVehiclesPVR
					maxFinancePVR
					maxTotalPVR
				}
				updatedAt
			}
		}
		${SalesMetricsFragment}
	`;

	const variables = {
		commissionCycleId,
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesLogAnalytics", SalesLogAnalytic> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSalesLogAnalytics;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
